import { supabase } from '@/integrations/supabase/client';
import { debug } from '@/lib/debug';
import type { EntitlementKey } from '@/types/entitlements';

export interface EntitlementRecord {
  id: string;
  org_id: string;
  key: string;
  value: unknown;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Parse the JSONB value field to a boolean
 * Handles: true, "true", JSON strings, etc.
 */
export function parseEntitlementValue(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) === true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Check if an entitlement is currently active (not expired)
 */
export function isEntitlementActive(entitlement: EntitlementRecord): boolean {
  const isEnabled = parseEntitlementValue(entitlement.value);
  if (!isEnabled) return false;
  
  if (entitlement.expires_at) {
    const expiresAt = new Date(entitlement.expires_at);
    if (expiresAt < new Date()) {
      return false; // Expired
    }
  }
  
  return true;
}

/**
 * Fetch all entitlements for a specific organization
 * Uses explicit org_id filter for reliability (not just RLS)
 */
export async function fetchOrgEntitlements(orgId: string): Promise<EntitlementRecord[]> {
  debug.log('[Entitlements] Fetching entitlements for org:', orgId);
  
  const { data, error } = await supabase
    .from('entitlements')
    .select('*')
    .eq('org_id', orgId);
  
  if (error) {
    console.error('[Entitlements] Error fetching entitlements:', error);
    return [];
  }
  
  debug.log('[Entitlements] Found entitlements:', data);
  return data || [];
}

/**
 * Process raw entitlements into a map of active entitlements
 * Returns both boolean flags and any numeric/string values
 */
export function processEntitlements(
  entitlements: EntitlementRecord[]
): Record<EntitlementKey, boolean | number | string> {
  const result: Record<EntitlementKey, boolean | number | string> = {
    full_access: false,
    beta_access: false,
    demo_mode: false,
    ai_assistant_access: false,
    prerelease_features: false,
    free_tier_override: false,
    grant_orbit_starter_tier: false,
    grant_orbit_tier: false,
    grant_orbit_business_tier: false,
    // Intake form entitlements
    intake_forms_enabled: false,
    intake_form_limit: 0,
    intake_form_custom_fields: false,
    intake_form_logic: false,
    intake_form_templates: false,
    intake_form_automation: false,
    intake_form_ai_insights: false,
    intake_form_team_routing: false,
    intake_form_conversion_analytics: false,
  };
  
  for (const entitlement of entitlements) {
    const key = entitlement.key as EntitlementKey;
    if (key in result) {
      // Check if this is a numeric entitlement (like intake_form_limit)
      if (key === 'intake_form_limit') {
        const parsedValue = parseEntitlementValue(entitlement.value);
        if (typeof entitlement.value === 'number') {
          result[key] = isEntitlementActive(entitlement) ? entitlement.value : 0;
        } else if (parsedValue) {
          // If true, set to Infinity (unlimited)
          result[key] = Infinity;
        }
      } else {
        result[key] = isEntitlementActive(entitlement);
      }
    }
  }
  
  debug.log('[Entitlements] Processed entitlements:', result);
  return result;
}
